import React from "react";
import {
  GaugeThreshold,
  describeAnnularArc,
  describeRoundedAnnularArc,
  defaultColors,
  getValueColorFromThresholds,
  formatGaugeValue,
  valueToRingAngle,
  RING_VIEW_WIDTH,
  RING_VIEW_HEIGHT,
  RING_CX,
  RING_CY,
  RING_OUTER_R,
  SR_ONLY_STYLE,
  getContrastTextColor,
} from "./gaugeUtils";

export interface ConcentricRingValue {
  value: number;
  max: number;
  color: string;
  label: string;
}

export interface RingRendererProps {
  // Data
  value: number;
  displayValue: number;
  min: number;
  max: number;
  hasValue: boolean;

  // Colors (resolved — no CSS variables)
  resolvedColors: string[];
  resolvedThresholds: GaugeThreshold[];
  valueColor: string;

  // Ring-specific
  ringThickness: number;
  startAngle: number;
  concentricValues?: ConcentricRingValue[];
  ringGap: number;

  // Display
  label: string;
  unit: string;
  valueFormat: string;
  showValue: boolean;
  showLabel: boolean;
  valueFontSize: number;
  labelFontSize: number;

  // Styling
  segmentGap: number;
  roundedSegments: boolean;
  segmentOpacity: number;

  // Accessibility
  ariaLabel?: string;
}

/**
 * Pure ring (full 360° circle) gauge renderer.
 * No hooks, no state, no DOM access — receives all data as props.
 */
export const RingRenderer: React.FC<RingRendererProps> = ({
  value,
  displayValue,
  min,
  max,
  hasValue,
  resolvedColors,
  resolvedThresholds,
  valueColor,
  ringThickness,
  startAngle,
  concentricValues,
  ringGap,
  label,
  unit,
  valueFormat,
  showValue,
  showLabel,
  valueFontSize,
  labelFontSize,
  roundedSegments,
  segmentOpacity,
  ariaLabel,
}) => {
  const hasThresholds = resolvedThresholds.length > 0;
  const trackColor = resolvedColors[1] || defaultColors[1];
  const fillColor = hasThresholds
    ? getValueColorFromThresholds(value, resolvedThresholds, resolvedColors[0] || defaultColors[0])
    : (resolvedColors[0] || defaultColors[0]);

  // Contrast-safe text color: when threshold colors make valueColor match the fill,
  // swap to a contrast-safe alternative to maintain 4.5:1 ratio (WCAG 2.1 AA)
  const effectiveValueColor = valueColor === fillColor
    ? getContrastTextColor(fillColor)
    : valueColor;

  const renderRing = (
    outerR: number,
    innerR: number,
    ringValue: number,
    ringMin: number,
    ringMax: number,
    ringFillColor: string,
    ringTrackColor: string,
    index: number,
    ringLabel?: string,
  ) => {
    // Track: full circle (clamped to 359.99° to avoid SVG degenerate arc)
    const trackPath = describeAnnularArc(
      RING_CX, RING_CY, innerR, outerR,
      startAngle, startAngle - 359.99
    );

    // Fill arc from startAngle to value angle
    const endAngle = valueToRingAngle(ringValue, ringMin, ringMax, startAngle);
    const sweep = startAngle - endAngle;

    let fillPath: string | null = null;
    if (Math.abs(sweep) > 0.01) {
      const clampedEnd = Math.abs(sweep) >= 359.99
        ? startAngle - 359.99
        : endAngle;
      const cornerR = Math.min(7, (outerR - innerR) / 2);

      fillPath = roundedSegments
        ? describeRoundedAnnularArc(
            RING_CX, RING_CY, innerR, outerR,
            startAngle, clampedEnd,
            true, true, cornerR
          )
        : describeAnnularArc(RING_CX, RING_CY, innerR, outerR, startAngle, clampedEnd);
    }

    return (
      <g key={`ring-${index}`}>
        <path d={trackPath} fill={ringTrackColor} opacity={0.2} />
        {fillPath && (
          <path
            d={fillPath}
            fill={ringFillColor}
            opacity={segmentOpacity}
            style={{ transition: 'fill 300ms ease' }}
          >
            {ringLabel && <title>{ringLabel}</title>}
          </path>
        )}
      </g>
    );
  };

  const isConcentricMode = concentricValues && concentricValues.length > 0;
  const ringsToRender = isConcentricMode ? concentricValues.slice(0, 3) : null;

  // Value display text
  let displayText: string;
  if (!hasValue) {
    displayText = '--';
  } else if (Number.isNaN(value) || Number.isNaN(displayValue)) {
    displayText = 'N/A';
  } else {
    displayText = formatGaugeValue(valueFormat, displayValue, max);
  }

  return (
    <div
      data-testid="gauge-ring-renderer"
      role="progressbar"
      aria-valuenow={hasValue && !Number.isNaN(value) ? value : undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={ariaLabel || (label ? `${label}: ${displayText}` : `Progress: ${displayText}`)}
      style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${RING_VIEW_WIDTH} ${RING_VIEW_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        data-testid="gauge-ring-svg"
      >
        {isConcentricMode ? (
          ringsToRender!.map((ring, i) => {
            const outerR = RING_OUTER_R - i * (ringThickness + ringGap);
            const innerR = outerR - ringThickness;
            if (innerR <= 0) return null;
            const ringVal = (ring.value == null || Number.isNaN(ring.value)) ? 0 : ring.value;
            return renderRing(outerR, innerR, ringVal, 0, ring.max, ring.color, trackColor, i, `${ring.label}: ${ringVal} / ${ring.max}`);
          })
        ) : (
          renderRing(RING_OUTER_R, RING_OUTER_R - ringThickness, value, min, max, fillColor, trackColor, 0, `${label || 'Progress'}: ${displayValue} / ${max}${unit ? ' ' + unit : ''}`)
        )}

        {/* Center value text */}
        {showValue && (
          <text
            x={RING_CX}
            y={showLabel && label ? RING_CY - 4 : RING_CY + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={valueFontSize}
            fontWeight="700"
            fill={effectiveValueColor}
            data-testid="gauge-ring-value"
          >
            {displayText}
            {hasValue && !Number.isNaN(displayValue) && unit && (
              <tspan fontSize={Math.round(valueFontSize * 0.625)}>{unit}</tspan>
            )}
          </text>
        )}

        {/* Center label text */}
        {showLabel && label && (
          <text
            x={RING_CX}
            y={RING_CY + valueFontSize * 0.6 + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={labelFontSize}
            fontWeight="600"
            fill={effectiveValueColor}
            data-testid="gauge-ring-label"
          >
            {label.length > 16 ? (
              <>
                <title>{label}</title>
                {label.substring(0, 15) + '\u2026'}
              </>
            ) : label}
          </text>
        )}
      </svg>

      {/* Legend for concentric rings (2+ entries) — shows ALL entries, not just rendered rings */}
      {isConcentricMode && concentricValues!.length >= 2 && (
        <div
          data-testid="gauge-ring-legend"
          style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '4px' }}
        >
          {concentricValues!.map((ring, i) => {
            const ringDisplayVal = (ring.value == null || Number.isNaN(ring.value)) ? '--' : ring.value;
            return (
              <div key={`legend-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ring.color }} />
                <span style={{ color: valueColor }}>{ring.label}: {ringDisplayVal}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Screen-reader descriptions for concentric rings */}
      {isConcentricMode && (
        <div style={SR_ONLY_STYLE} data-testid="gauge-ring-sr-descriptions">
          {concentricValues!.map((ring, i) => {
            const ringVal = (ring.value == null || Number.isNaN(ring.value)) ? 'no data' : ring.value;
            return (
              <span key={`sr-ring-${i}`} data-testid={`gauge-ring-sr-item-${i}`}>
                {ring.label}: {ringVal} of {ring.max}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
